import { usePage } from '@inertiajs/vue3';
import { computed } from 'vue';
import type { ComputedRef } from 'vue';
import {
    buildLegalPageJsonLd,
    ogAlternateLocale,
    ogPrimaryLocale,
} from '@/lib/seoJsonLd';
import { dashboard } from '@/routes';

export type LegalDocument = 'about' | 'privacy' | 'terms';

export type LegalPageSeoResult = {
    headTitle: ComputedRef<string>;
    metaDescription: ComputedRef<string>;
    ogTitle: ComputedRef<string>;
    ogDescription: ComputedRef<string>;
    absolutePageUrl: ComputedRef<string>;
    ogLocale: ComputedRef<string>;
    ogLocaleAlternate: ComputedRef<string>;
    jsonLd: ComputedRef<string>;
    siteDisplayName: ComputedRef<string>;
};

export function useLegalPageSeo(
    document: LegalDocument,
    t: (key: string, fallback?: string) => string,
): LegalPageSeoResult {
    const page = usePage();
    const seoPrefix = `legal.${document}.seo`;

    const absoluteBaseUrl = computed(() =>
        String(page.props.absoluteBaseUrl ?? '').replace(/\/$/, ''),
    );

    const pathOnly = computed(() => {
        const raw = String(page.url ?? '');
        const q = raw.split('?')[0] ?? raw;

        return q.startsWith('/') ? q : `/${q}`;
    });

    const absolutePageUrl = computed(
        () => `${absoluteBaseUrl.value}${pathOnly.value}`,
    );

    const p = (suffix: string) => `${seoPrefix}.${suffix}`;

    const fallbackTitle = computed(() =>
        t(`legal.${document}.title`, document),
    );

    const headTitle = computed(() => t(p('head_title'), fallbackTitle.value));

    const metaDescription = computed(() => t(p('meta_description'), ''));

    const ogTitle = computed(() => t(p('og_title'), headTitle.value));

    const ogDescription = computed(() =>
        t(p('og_description'), metaDescription.value),
    );

    const siteDisplayName = computed(() =>
        String(page.props.siteDisplayName ?? ''),
    );

    const ogLocale = computed(() => ogPrimaryLocale(page.props.locale));

    const ogLocaleAlternate = computed(() =>
        ogAlternateLocale(page.props.locale),
    );

    const siteHomeUrl = computed(
        () => `${absoluteBaseUrl.value}${dashboard.url()}`,
    );

    const jsonLd = computed(() =>
        buildLegalPageJsonLd({
            pageUrl: absolutePageUrl.value,
            name: ogTitle.value,
            description: metaDescription.value,
            siteName: siteDisplayName.value,
            siteHomeUrl: siteHomeUrl.value,
        }),
    );

    return {
        headTitle,
        metaDescription,
        ogTitle,
        ogDescription,
        absolutePageUrl,
        ogLocale,
        ogLocaleAlternate,
        jsonLd,
        siteDisplayName,
    };
}
